import type { AnalyticsInstance, AnalyticsRegionStats, AnalyticsResponse, AnalyticsInstancesResponse } from "./analytics";

type CreditFields = {
  billedMinutes?: number;
  creditsBilledMinutes?: number;
  onDemandBilledMinutes?: number;
  subscriptionBilledMinutes?: Record<string, number>;
};
type ExactCreditFields = {
  billedCredits?: string;
  creditsBilled?: string;
  onDemandBilledCredits?: string;
  subscriptionBilledCredits?: Record<string, string>;
};

// Convert only for charts and display. Accounting arithmetic stays on the server.
export function displayCreditAmounts<T extends CreditFields>(value: T): T {
  const exact = value as T & ExactCreditFields;
  return {
    ...value,
    ...(exact.billedCredits !== undefined ? { billedMinutes: Number(exact.billedCredits) } : {}),
    ...(exact.creditsBilled !== undefined ? { creditsBilledMinutes: Number(exact.creditsBilled) } : {}),
    ...(exact.onDemandBilledCredits !== undefined ? { onDemandBilledMinutes: Number(exact.onDemandBilledCredits) } : {}),
    ...(exact.subscriptionBilledCredits ? {
      subscriptionBilledMinutes: Object.fromEntries(Object.entries(exact.subscriptionBilledCredits).map(([id, amount]) => [id, Number(amount)])),
    } : {}),
  };
}

function displayInstance(value: AnalyticsInstance): AnalyticsInstance {
  return { ...displayCreditAmounts(value), ...(value.billedBreakdown ? { billedBreakdown: displayCreditAmounts(value.billedBreakdown) } : {}) };
}

function displayRegions(regions: Record<string, AnalyticsRegionStats>) {
  return Object.fromEntries(Object.entries(regions).map(([region, stats]) => [region, displayCreditAmounts(stats)]));
}

function displayPlatforms<T extends AnalyticsResponse["summary"]>(value: T): T {
  return { ...value, android: displayRegions(value.android), ios: displayRegions(value.ios), xcode: displayRegions(value.xcode), gradle: displayRegions(value.gradle) };
}

export function displayAnalyticsCredits(value: AnalyticsResponse): AnalyticsResponse {
  return {
    ...value,
    ...(value.summary ? { summary: displayPlatforms(value.summary) } : {}),
    series: value.series.map((bucket) => ({ ...displayPlatforms(bucket), ...(bucket.instances ? { instances: bucket.instances.map(displayInstance) } : {}) })),
  };
}

export function displayInstanceCredits(value: AnalyticsInstancesResponse): AnalyticsInstancesResponse {
  return { ...value, series: value.series.map((bucket) => ({ ...bucket, instances: bucket.instances.map(displayInstance) })) };
}
